import * as vp from 'vopay';
import { sanitizeToNumeric } from 'vopay';
import type { ApprovalTier } from './ApprovalTiers/ApprovalTiers';
import type { Indicator } from '@/providers/DataFetch.provider';

export type RailStats = { created: number; replaced: number };

type Deps = {
  setDataFetchingInfo: (loading: boolean, message?: string, indicator?: Indicator) => void;
  fetchSubAccountsAsync: (params: vp.SubAccountsRequest, bearer?: string) => Promise<vp.SubAccountsResponse>;
  fetchWorkflows: (bearer?: string) => Promise<vp.WorkflowsResponse | undefined>;
  createWorkFlowAsync: (params: vp.WorkflowCreateRequest, bearer?: string) => Promise<vp.ApiResponse>;
  deleteWorkflowAsync: (params: vp.WorkflowDeleteRequest, bearer?: string) => Promise<vp.ApiResponse>;
  accountId?: string;
};

export async function propagateToSubAccounts(
  transactionTypes: string[],
  tiers: ApprovalTier[],
  deps: Deps,
): Promise<{
  perRail: Record<string, RailStats>;
  failedPermission: number;
  totalSubAccounts: number;
  failedSubAccountNames: string[];
}> {
  const {
    setDataFetchingInfo,
    fetchSubAccountsAsync,
    fetchWorkflows,
    createWorkFlowAsync,
    deleteWorkflowAsync,
    accountId,
  } = deps;

  const emptyRail = (): Record<string, RailStats> =>
    Object.fromEntries(transactionTypes.map((tt) => [tt, { created: 0, replaced: 0 }]));

  setDataFetchingInfo(true, '', 'fullScreen');

  const subAccountsRes = await fetchSubAccountsAsync({
    ShowToken: true,
    ActiveOnly: true,
    ...(accountId ? { SubaccountID: accountId } : {}),
  });
  if (!subAccountsRes.Success)
    return { perRail: emptyRail(), failedPermission: 0, totalSubAccounts: 0, failedSubAccountNames: [] };

  const subAccounts = subAccountsRes.Subaccounts ?? [];

  const results = await Promise.all(
    subAccounts.map(async (subAccount) => {
      const bearer = subAccount.Token;
      const localRail = emptyRail();
      let failed = false;

      try {
        const workflowsRes = await fetchWorkflows(bearer);
        const existingGroups = workflowsRes?.Workflows ?? [];

        await Promise.all(
          transactionTypes.map(async (transactionType) => {
            try {
              const existingGroup = existingGroups.find((g) => g.TransactionType === transactionType);
              const createTiers = () =>
                Promise.all(
                  tiers.map(async (tier) => {
                    const res = await createWorkFlowAsync(
                      {
                        TransactionType: transactionType,
                        NumberApprovalsRequired: sanitizeToNumeric(tier.approversNeeded) || '0',
                        MinAmount: sanitizeToNumeric(tier.startAmount || '0') || '0',
                        MaxAmount: tier.noUpperLimit ? null : sanitizeToNumeric(tier.endAmount),
                        ApplyToAllSubaccounts: true,
                      },
                      bearer,
                    );
                    if (!res.Success) throw new Error(res.ErrorMessage);
                  }),
                );

              if (existingGroup) {
                await Promise.all(
                  existingGroup.Workflows.map(async (wf) => {
                    const res = await deleteWorkflowAsync({ WorkflowID: wf.WorkflowID }, bearer);
                    if (!res.Success) throw new Error(res.ErrorMessage);
                  }),
                );
                await createTiers();
                localRail[transactionType].replaced++;
              } else {
                await createTiers();
                localRail[transactionType].created++;
              }
            } catch {
              failed = true;
            }
          }),
        );
      } catch {
        failed = true;
      }

      return { localRail, failed, accountName: subAccount.AccountName };
    }),
  );

  const perRail = emptyRail();
  let failedPermission = 0;
  const failedSubAccountNames: string[] = [];

  for (const { localRail, failed, accountName } of results) {
    if (failed) {
      failedPermission++;
      if (accountName) failedSubAccountNames.push(accountName);
    }
    for (const tt of transactionTypes) {
      perRail[tt].created += localRail[tt].created;
      perRail[tt].replaced += localRail[tt].replaced;
    }
  }

  setDataFetchingInfo(false);
  return { perRail, failedPermission, totalSubAccounts: subAccounts.length, failedSubAccountNames };
}
